import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { useAuth } from "../context/AuthContext";
import { Brand } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";

const PERKS = [
  "Free account with no expiration — no card required",
  "1 active credit application, 10 archived* credit applications, unlimited references",
  "* - Archived credit applications may be purged by the subscribing company as needed",
  "One-tap reference links and automatic chasing",
  "Branded credit packet PDF you can file",
];

export default function Signup() {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    password: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/public/signup", {
        ...form,
        origin_url: window.location.origin,
      });
      adopt(data.user);
      toast.success(`Welcome to ConfirmTerms, ${form.company_name}. Your free account is ready.`);
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = "text", required = true, placeholder = "") => (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </Label>
      <Input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) =>
          type === "tel" ? setForm({ ...form, [key]: formatPhone(e.target.value) }) : set(key)(e)
        }
        placeholder={placeholder}
        data-testid={`signup-${key.replace("_", "-")}-input`}
        className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
      />
    </div>
  );

  const passwordField = (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        Choose a password
      </Label>
      <PasswordInput
        required
        minLength={8}
        value={form.password}
        onChange={set("password")}
        placeholder="At least 8 characters"
        testId="signup-password-input"
        className="mt-2"
      />
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <div className="flex w-full flex-col justify-center px-6 py-14 sm:px-12 lg:w-[56%] lg:px-20">
        <div className="ct-rise mx-auto w-full max-w-md">
          <Brand />
          <h1 className="mt-12 font-display text-4xl font-black leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
            Start your free account
          </h1>
          <p className="mt-4 text-base text-slate-600">
            Create your workspace and start collecting verified trade references in minutes.
          </p>

          <form onSubmit={submit} className="mt-9 space-y-5" data-testid="signup-form">
            {field("company_name", "Company name", "text", true, "Acme Distribution")}
            {field("contact_name", "Your name", "text", true, "Jordan Blake")}
            {field("email", "Work email", "email", true, "you@company.com")}
            {field("phone", "Phone", "tel", false, "555-010-2020")}
            {passwordField}
            {error && (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                data-testid="signup-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="signup-submit-button"
              className="h-11 w-full rounded-full bg-blue-600 text-[15px] font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-[0.99]"
            >
              {busy ? "Creating your workspace…" : "Create my workspace"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="signup-signin-link"
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="relative hidden bg-[#0d1b3e] lg:block lg:w-[44%]">
        <div className="absolute inset-0 flex flex-col justify-center px-14">
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-300">
            ConfirmTerms.com
          </div>
          <p className="mt-5 font-display text-2xl font-extrabold leading-snug tracking-tight text-white">
            Know who you are extending credit to.
          </p>
          <ul className="mt-9 space-y-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" />
                <span className="text-sm text-slate-200">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
